/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  CellType,
  ComponentType,
  IMediaModel,
  IPersonModel,
  Orientation,
} from "@nf/common";
import { useTranslation } from "next-i18next";

import { ListComponentHorizontalInteractive } from "components/Configuration";
import DotsLoader from "components/DotsLoader";
import GridComponent from "components/Configuration/ListComponent/components/GridComponent";
import ListComponentPersonItemFrame from "components/Configuration/ListComponent/components/ListComponentItem/components/ListComponentPersonItemFrame";
import SearchNoResults from "../SearchNoResults";

import searchStyles from "../SearchScreen.module.scss";
import styles from "./SearchResults.module.scss";

interface SearchResultsProps {
  person?: IPersonModel;
  people: IPersonModel[];
  assets: IMediaModel[];
  isLoading: boolean;
  onPersonClick: (index: number, person: IPersonModel) => void;
}

const SearchResults = ({
  person,
  people,
  assets,
  isLoading,
  onPersonClick,
}: SearchResultsProps) => {
  const { t } = useTranslation("translations");
  const selectedPersonIndex = people.findIndex(
    (p) => p.PersonId === person?.PersonId
  );

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <DotsLoader width={100} />
      </div>
    );
  }

  if (!assets.length && !people.length) {
    return (
      <div className={searchStyles.pagePadding}>
        <SearchNoResults />
      </div>
    );
  }

  return (
    <div>
      {people.length > 0 && (
        <>
          <h2 className={`${styles.title} ${searchStyles.pagePadding}`}>
            {t("web_search_results_header_persons")}
          </h2>

          <ListComponentHorizontalInteractive
            className={styles.people}
            initialActiveIndex={selectedPersonIndex}
            component={{
              Id: "people",
              ComponentTypeCode: ComponentType.List,
              CellType: CellType.Custom,
              Orientation: Orientation.Horizontal,
              // TODO list component is typed for media elements only
              // it might be a good idea to make it more generic so
              // other data such as person could be used
              // @ts-ignore
              MediaList: people,
              VisibleItemsCount: 3,
              CutomListItem: <ListComponentPersonItemFrame />,
            }}
            // TODO list component is typed for media elements only
            // it might be a good idea to make it more generic so
            // other data such as person could be used
            // @ts-ignore
            onItemClick={onPersonClick}
          />
        </>
      )}

      <h2 className={`${styles.title} ${searchStyles.pagePadding}`}>
        {person
          ? `${t("web_search_results_movies_selected", {
              name: person.PersonFullName,
            })}`
          : `${t("web_search_results_movies_unselected")}`}
      </h2>

      {assets.length > 0 ? (
        <GridComponent
          className={searchStyles.pagePadding}
          component={{
            ComponentTypeCode: ComponentType.List,
            CellType: CellType.Frame,
            Orientation: Orientation.Grid,
            MediaList: assets,
          }}
        />
      ) : (
        <div className={searchStyles.pagePadding}>
          <SearchNoResults />
        </div>
      )}
    </div>
  );
};

export default SearchResults;
